<template>
  <sp-modal
    width="var(--waitlist-modal-width)"
    persistent
    closeable
    no-bottom-sheet
    no-mobile-content-padding
    :open="open"
    :modal-title="modalTitle"
    no-scrollable-content
    :class="classModifiers"
    @update-open="handleModalUpdateOpen"
    @close="handleModalClose"
    @keydown.enter="submitForm"
  >
    <button
      v-if="isActivatorAsLink"
      ref="activator"
      slot="activator"
      type="button"
      class="activator-link"
      :data-testid="TestId.events.waitlistJoin"
    >
      {{ activatorText }}
    </button>
    <sp-button
      v-else
      ref="activator"
      slot="activator"
      class="activator"
      block-sm
      :color="activatorColor"
      :variant="activatorVariant"
      :data-testid="TestId.events.waitlistJoin"
    >
      {{ activatorText }}
    </sp-button>

    <sp-alert v-if="error" slot="alert" type="error" variant="elevated" closeable @close="error = null">
      {{ error }}
    </sp-alert>

    <WaitlistPanel :preamble="preamble" :info="info">
      <div ref="slotContainer" class="form-slot">
        <slot name="form"></slot>
      </div>
    </WaitlistPanel>

    <sp-container slot="actions" flex>
      <sp-spacer></sp-spacer>
      <sp-button color="primary" :loading="loading" :data-testid="TestId.events.waitlistSubmit" @click="submitForm">
        Join waitlist
      </sp-button>
    </sp-container>
  </sp-modal>
</template>

<script setup>
import { useEventListener } from "@vueuse/core";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";
import { TestId } from "#/test-ids";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useExpose } from "#ce/composables/useExpose";
import { createCtaActivator } from "#ce/utils/cta";
import { toBoolean } from "#ce/utils/props";
import WaitlistPanel from "./components/WaitlistPanel.ce.vue";
import { useWaitlistStore } from "./store/waitlist";
import { props as propsDefinitions } from "./props.js";

const emit = defineEmits(["update-open"]);

const props = defineProps(propsDefinitions);

const isActivatorAsLink = computed(() => toBoolean(props.activatorAsLink));

const waitlistStore = useWaitlistStore();

const open = ref(false);
const loading = ref(false);
const error = ref(null);

const activator = useTemplateRef("activator");

const slotContainer = useTemplateRef("slotContainer");
const formSlot = ref(null);
const formElement = ref(null);

const { exposeProperties } = useExpose();

watch(open, (value) => emit("update-open", value));

/**
 * Returns the CTA activator element
 * This is used to expose the activator button to the parent component, so it can be used to open the modal from
 * outside the component.
 */
function getCtaActivator() {
  const ctaActivator = createCtaActivator(props.mobileCtaActivatorText ?? props.activatorText);
  useEventListener(ctaActivator, "click", () => (open.value = true));
  return ctaActivator;
}

exposeProperties({
  ctaActivator: {
    get: getCtaActivator,
  },
});

watch(slotContainer, (value) => {
  formSlot.value = value?.querySelector("slot[name=form]");
});

/**
 * In order to access the form element, we need to wait for the slot to be populated.
 * We listen for the slotchange event, extract the form element, and store it in the form element ref.
 */
useEventListener(formSlot, "slotchange", () => {
  const slotContent = formSlot.value?.assignedElements?.()?.at(0);
  formElement.value = slotContent?.querySelector("form");
  removeUnwantedFormElements();
  waitlistStore.setFormElement(formElement.value);
});

function removeUnwantedFormElements() {
  formElement.value?.querySelector("[type=submit]")?.remove();
  formElement.value?.querySelector("[data-behaviour=preview-form]")?.remove();
}

function handleModalUpdateOpen({ detail }) {
  const [value] = detail;
  open.value = value;
}

function handleModalClose() {
  reset();
}

async function submitForm() {
  if (!waitlistStore.isValid()) {
    return;
  }

  try {
    loading.value = true;
    const confirmationUrl = await waitlistStore.submitForm();
    open.value = false;
    nextTick(() => (document.location.href = confirmationUrl));
  } catch (err) {
    console.error(err.message);
    error.value = err?.message ?? "An error occurred. Please try again later.";
    loading.value = false;
  }
}

function reset() {
  waitlistStore.reset();
  loading.value = false;
  error.value = null;
}

const { breakpointModifiers } = useBreakpoints();
const classModifiers = computed(() => [...breakpointModifiers.value]);
</script>

<style>
:host {
  --header-background: var(--sp-sys-layout-hero-before-content-background, #eee);
  --header-row-gap: var(--sp-ref-scale-24);
  --card-padding-inline: var(--sp-comp-card-padding-inline, 1.5rem);
  --offset-inline: calc(var(--card-padding-inline) * -1);
  --card-padding-block: var(--sp-comp-card-padding-block, 0.5rem);
  --offset-block: calc(var(--card-padding-block) * -1);
  --dialog-height: 94vh;
  --dialog-min-height: 44rem;
  --dialog-max-height: 58rem;
  --sp-comp-dialog-content-height: 100%;

  display: block;
}

sp-modal {
  --waitlist-modal-width: 40rem;
  /* Fills the fixed-height dialog (--dialog-height / -max-height). Both the card
     height and max-height are 100% because max-height overrides height in CSS. */
  --sp-comp-modal-card-height: 100%;
  --sp-comp-modal-card-max-height: 100%;

  &.--is-sm-screen {
    --waitlist-modal-width: calc(100dvw - var(--sp-ref-scale-16));
  }
}

sp-heading[level="2"] {
  margin-top: calc(var(--sp-ref-scale-16) * 3.625);
  margin-bottom: calc(var(--sp-ref-scale-16) * 1.625);
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.activator {
  --bg-color: var(--sp-comp-waitlist-activator-bg-color, var(--sp-sys-color-warning));
  --hover-bg-color: var(--sp-comp-waitlist-activator-hover-bg-color, var(--bg-color));
  --text-color: var(--sp-comp-waitlist-activator-text-color, var(--sp-sys-color-on-surface));
  --hover-text-color: var(--sp-comp-waitlist-activator-hover-text-color, var(--text-color));
  --activator-min-width: var(--sp-comp-waitlist-activator-width, 100%);
  --sp-comp-button-width: var(--activator-min-width);
  --sp-comp-button-min-width: var(--activator-min-width);

  display: flex;
  place-self: var(--sp-comp-waitlist-activator-place-self, auto);
  min-width: var(--activator-min-width);
}

.activator-link {
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: center;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
}

.form-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: var(--sp-ref-scale-24);
}
.actions {
  display: flex;
  width: 100%;
}
</style>
